import React, { useState, useEffect } from "react";
import { C, fmt, statusLabel, text } from "../theme";
import { api } from "../api";
import { usePermissions } from "../PermissionContext";

import { Card, Btn, Notice, StatusPill as Badge, ConfirmDialog, DocLink, rowAction } from "../components/ui";
import DateField from "../components/DateField";

const cell = { padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" };
const inputStyle = { width: "100%", padding: 8, border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md, fontFamily: "inherit", boxSizing: "border-box" };
const label = { fontSize: text.xs, fontWeight: 700, color: C.gray500, textTransform: "uppercase", display: "block", marginBottom: 4 };

const toDateOnly = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "");
// Format a date/ISO as dd/mm/yyyy (UTC parts keep the stored 23:59Z date stable).
const fmtDMY = (v) => {
  if (!v) return "";
  const d = new Date(v);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

// Soft guidance box for the tender-threshold policy (UX-10).
function PolicyHint({ tone = "info", children }) {
  const color = tone === "warn" ? C.warning : C.gray600;
  const bg = tone === "warn" ? C.warningBg : C.gray50;
  return (
    <div style={{ gridColumn: "1 / -1", padding: "10px 14px", borderRadius: 8, background: bg, fontSize: text.sm, color, fontWeight: 600, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

export default function TenderRequestPage({ initialId, onOpenDoc }) {
  const { me, can } = usePermissions();
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(initialId || null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  // Owned here so a message set by the detail view survives navigating back.
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  const load = () => {
    setLoading(true);
    api.getTenderRequests().then(setRows).catch((e) => setNotice({ kind: "error", text: e.message })).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  if (creating) {
    return <CreateForm me={me} existing={rows} onCancel={() => setCreating(false)} onDone={(created) => {
      // Land the user on the created draft, not back on the list (UX-12/52).
      setCreating(false);
      setNotice({ kind: "success", text: `${created.requestNumber} created as a draft. Review Part I/II below, then Submit.` });
      setSel(created.id);
      load();
    }} />;
  }

  // A sel the loaded list doesn't contain (deep link outside the user's data
  // scope, or a stale id) falls through to the list view.
  const selTr = sel ? rows.find((x) => x.id === sel) : null;
  if (selTr) {
    return <Detail tr={selTr} me={me} can={can} onBack={() => setSel(null)} reload={load} notice={notice} notify={setNotice} onOpenDoc={onOpenDoc} />;
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: 0 }}>Tender Requests</h1>
          <p style={{ fontSize: text.md, color: C.gray500, margin: "2px 0 0" }}>{rows.length} records · ITQ/ITT publication requests raised from approved AORs</p>
        </div>
        {can("tenderRequest", "create") && (
          <div style={{ marginLeft: "auto" }}><Btn onClick={() => { setNotice(null); setCreating(true); }}>+ New Tender Request</Btn></div>
        )}
      </div>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Ref", "ITQ/ITT No.", "Type", "AOR", "Description", "Total", "Status"].map((h, i) => <th scope="col" key={i} style={cell}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} {...rowAction(() => { setNotice(null); setSel(t.id); })} style={{ cursor: "pointer", borderBottom: `1px solid ${C.gray100}` }}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: C.primary }}>{t.requestNumber}</td>
                <td style={{ padding: "10px 12px" }}>{t.itqNumber || "—"}</td>
                <td style={{ padding: "10px 12px" }}>{t.type}{t.urgent ? " ⚡" : ""}</td>
                <td style={{ padding: "10px 12px", fontSize: text.xs, color: C.primary }}>{t.aor?.aorNumber}</td>
                <td style={{ padding: "10px 12px" }}>{t.description}</td>
                <td style={{ padding: "10px 12px" }}>{fmt(t.total)}</td>
                <td style={{ padding: "10px 12px" }}><Badge status={t.status} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: C.gray500 }}>No tender requests yet — raise one from an approved AOR with + New Tender Request.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function CreateForm({ me, existing, onCancel, onDone }) {
  const [aors, setAors] = useState([]);
  const [form, setForm] = useState({
    aorId: "", type: "ITQ", urgent: false,
    contactName: me?.displayName || "", contactEmail: me?.email || "",
    publicationDate: "", description: "", budgetCapex: "", budgetOpex: "",
  });
  const [closing, setClosing] = useState({ iso: "", err: "" });
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }
  const [tenderThreshold, setTenderThreshold] = useState(null);
  // Tracks the AOR description last prefilled, so switching AORs replaces an
  // untouched prefill instead of silently keeping the old one (UX-30).
  const [prefilledDesc, setPrefilledDesc] = useState("");
  const usedAorIds = new Set(existing.map((r) => r.aorId));

  useEffect(() => {
    api.getAORs()
      .then((all) => setAors(all.filter((a) => a.status === "APPROVED" && !usedAorIds.has(a.id))))
      .catch((e) => setNotice({ kind: "error", text: e.message }));
    api.getProcurementPolicy().then((p) => setTenderThreshold(p.tenderThreshold)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derive the closing date (SG working days) whenever type or publication changes.
  useEffect(() => {
    const start = form.publicationDate ? form.publicationDate.slice(0, 10) : "";
    if (!start) { setClosing({ iso: "", err: "" }); return; }
    let cancelled = false;
    api.getTenderClosingDate(form.type, start)
      .then((r) => { if (!cancelled) setClosing({ iso: r.closingDate, err: "" }); })
      .catch((e) => { if (!cancelled) setClosing({ iso: "", err: e.message }); });
    return () => { cancelled = true; };
  }, [form.type, form.publicationDate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickAor = (id) => {
    const a = aors.find((x) => x.id === id);
    const isCapex = a ? /capital/i.test(a.budget?.category || "") : false;
    setForm((f) => ({
      ...f,
      aorId: id,
      // Prefill the description when the field is empty OR still holds the
      // previous AOR's untouched prefill (UX-30: no silent concatenation).
      description: a && (!f.description || f.description === prefilledDesc) ? a.description || "" : f.description,
      budgetCapex: a ? (isCapex ? a.total : 0) : "",
      budgetOpex: a ? (isCapex ? 0 : a.total) : "",
    }));
    setPrefilledDesc(a ? a.description || "" : "");
  };

  const selectedAor = aors.find((x) => x.id === form.aorId);
  // A split-funded AOR draws on several budget lines, each charged its own
  // share (budgetChecks); a single-budget AOR has just its header line.
  const aorLines = selectedAor
    ? (selectedAor.budgetChecks && selectedAor.budgetChecks.length ? selectedAor.budgetChecks : (selectedAor.budgetCheck ? [selectedAor.budgetCheck] : []))
    : [];
  const budgetLine = selectedAor
    ? (aorLines.length > 1
      ? aorLines.map((c) => [c.budgetRef, c.bu].filter(Boolean).join(" · ")).join("  +  ")
      : [selectedAor.budget?.ref || selectedAor.budgetRef, selectedAor.budget?.bu, selectedAor.budget?.category].filter(Boolean).join(" · "))
    : "";
  const amountLeft = selectedAor && selectedAor.budgetCheck && selectedAor.budgetCheck.available != null
    ? selectedAor.budgetCheck.available
    : null;

  const submit = async () => {
    if (!form.aorId) { setNotice({ kind: "error", text: "Select an approved AOR." }); return; }
    if (!form.publicationDate) { setNotice({ kind: "error", text: "Select a publication / start date." }); return; }
    try {
      const created = await api.createTenderRequest({
        aorId: form.aorId, type: form.type, urgent: form.urgent,
        contactName: form.contactName, contactEmail: form.contactEmail,
        publicationDate: form.publicationDate,
        description: form.description,
        budgetCapex: form.budgetCapex === "" ? null : Number(form.budgetCapex),
        budgetOpex: form.budgetOpex === "" ? null : Number(form.budgetOpex),
      });
      onDone(created);
    } catch (e) { setNotice({ kind: "error", text: e.message }); } // form input preserved
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <Btn variant="outline" onClick={onCancel}>← Back</Btn>
        <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>New Tender Request</h1>
      </div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={label} htmlFor="ntr-aor">Source AOR (approved, no existing request)</label>
            <select id="ntr-aor" value={form.aorId} onChange={(e) => pickAor(e.target.value)} style={inputStyle}>
              <option value="">— select an approved AOR —</option>
              {aors.map((a) => <option key={a.id} value={a.id}>{a.aorNumber} · {a.description} · {fmt(a.total)}</option>)}
            </select>
          </div>
          {/* Tender-threshold guidance (UX-10): warn when the source sits below
              the mandatory-tender threshold, confirm the path when above it. */}
          {selectedAor && tenderThreshold != null && (
            selectedAor.total < tenderThreshold ? (
              <PolicyHint tone="warn">
                ⚠ {selectedAor.aorNumber} is {fmt(selectedAor.total)} — below the {fmt(tenderThreshold)} tender threshold.
                A tender is optional at this value (an ITQ may still be run deliberately); the usual path is a direct PO from the approved AOR.
              </PolicyHint>
            ) : (
              <PolicyHint>
                ✓ {selectedAor.aorNumber} is {fmt(selectedAor.total)} — at or above the {fmt(tenderThreshold)} threshold, so the tender path is required. ITT is the usual type at this value.
              </PolicyHint>
            )
          )}
          <div style={{ gridColumn: "1 / -1" }}>
            <span style={label}>Budget line <span style={{ fontWeight: 600, textTransform: "none", color: C.gray500 }}>(carried from AOR)</span></span>
            <div style={{ ...inputStyle, background: C.gray100, color: budgetLine ? C.gray700 : C.gray400, display: "flex", alignItems: "center", minHeight: 19 }}>
              {budgetLine || "— select an AOR —"}
            </div>
          </div>
          <div>
            <span style={label}>Approved budget — Capex <span style={{ fontWeight: 600, textTransform: "none", color: C.gray500 }}>(from AOR)</span></span>
            <div style={{ ...inputStyle, background: C.gray100, color: selectedAor ? C.gray700 : C.gray400, display: "flex", alignItems: "center", minHeight: 19 }}>
              {selectedAor ? fmt(form.budgetCapex) : "—"}
            </div>
          </div>
          <div>
            <span style={label}>Approved budget — Opex <span style={{ fontWeight: 600, textTransform: "none", color: C.gray500 }}>(from AOR)</span></span>
            <div style={{ ...inputStyle, background: C.gray100, color: selectedAor ? C.gray700 : C.gray400, display: "flex", alignItems: "center", minHeight: 19 }}>
              {selectedAor ? fmt(form.budgetOpex) : "—"}
            </div>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <span style={label}>Amount left for utilization <span style={{ fontWeight: 600, textTransform: "none", color: C.gray500 }}>(budget line availability, excl. this AOR)</span></span>
            <div style={{ ...inputStyle, background: C.gray100, color: amountLeft != null ? (aorLines.some((c) => c.available < 0) || amountLeft < 0 ? C.danger : C.gray700) : C.gray400, display: "flex", alignItems: "center", minHeight: 19 }}>
              {aorLines.length > 1
                ? aorLines.map((c) => `${c.budgetRef}: ${fmt(c.available)}`).join("  ·  ")
                : amountLeft != null ? fmt(amountLeft) : "— select an AOR —"}
            </div>
          </div>
          <div>
            <label style={label} htmlFor="ntr-type">Type</label>
            <select id="ntr-type" value={form.type} onChange={(e) => set("type", e.target.value)} style={inputStyle}>
              <option value="ITQ">ITQ — Invitation to Quote</option>
              <option value="ITT">ITT — Invitation to Tender</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <label style={{ fontSize: text.md, display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={form.urgent} onChange={(e) => set("urgent", e.target.checked)} />
              Urgent (&lt;5 working days — staff posts personally; Part II optional)
            </label>
          </div>
          <div><label style={label} htmlFor="ntr-contact-name">Contact name</label><input id="ntr-contact-name" value={form.contactName} onChange={(e) => set("contactName", e.target.value)} style={inputStyle} /></div>
          <div><label style={label} htmlFor="ntr-contact-email">Contact email</label><input id="ntr-contact-email" value={form.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} style={inputStyle} /></div>
          <div><label style={label} htmlFor="ntr-publication-date">Publication / start date</label><DateField id="ntr-publication-date" value={form.publicationDate} onChange={(v) => set("publicationDate", v)} /></div>
          <div>
            <span style={label}>Closing date <span style={{ fontWeight: 600, textTransform: "none", color: C.gray500 }}>({form.type} — {form.type === "ITT" ? 14 : 7} SG working days, 23:59)</span></span>
            <div style={{ ...inputStyle, background: C.gray100, color: closing.err ? C.danger : (closing.iso ? C.gray700 : C.gray400), display: "flex", alignItems: "center", minHeight: 19 }}>
              {closing.err || (closing.iso ? `${fmtDMY(closing.iso)} 23:59` : "— set a publication date —")}
            </div>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={label} htmlFor="ntr-description">Description of product / service <span style={{ fontWeight: 600, textTransform: "none", color: C.gray500 }}>(prefilled from the selected AOR — edit freely)</span></label>
            <textarea id="ntr-description" value={form.description} onChange={(e) => set("description", e.target.value)} style={{ ...inputStyle, minHeight: 70 }} />
          </div>
        </div>
      </Card>
      {/* Anchored to the action row so errors appear where the user acted. */}
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <Btn onClick={submit}>Create draft</Btn>
      <span style={{ marginLeft: 10, fontSize: text.sm, color: C.gray500 }}>Part II announcement is auto-drafted from these fields and can be edited next.</span>
    </div>
  );
}

function Detail({ tr, me, can, onBack, reload, notice, notify, onOpenDoc }) {
  const isDraft = tr.status === "DRAFT";
  const [confirming, setConfirming] = useState(null); // "post" | "cancel" | "build" | null
  const [form, setForm] = useState({
    type: tr.type, urgent: tr.urgent,
    contactName: tr.contactName || "", contactEmail: tr.contactEmail || "",
    publicationDate: toDateOnly(tr.publicationDate),
    description: tr.description || "", budgetCapex: tr.budgetCapex ?? "", budgetOpex: tr.budgetOpex ?? "",
    announcement: tr.announcement || "",
  });
  const [closing, setClosing] = useState({ iso: tr.closingDate || "", err: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // In draft, re-derive the closing date when type or publication changes.
  useEffect(() => {
    if (!isDraft) return undefined;
    const start = form.publicationDate ? form.publicationDate.slice(0, 10) : "";
    if (!start) { setClosing({ iso: "", err: "" }); return undefined; }
    let cancelled = false;
    api.getTenderClosingDate(form.type, start)
      .then((r) => { if (!cancelled) setClosing({ iso: r.closingDate, err: "" }); })
      .catch((e) => { if (!cancelled) setClosing({ iso: "", err: e.message }); });
    return () => { cancelled = true; };
  }, [form.type, form.publicationDate, isDraft]);

  const save = async () => {
    try {
      await api.updateTenderRequest(tr.id, {
        type: form.type, urgent: form.urgent, contactName: form.contactName, contactEmail: form.contactEmail,
        publicationDate: form.publicationDate || null,
        description: form.description, announcement: form.announcement,
        budgetCapex: form.budgetCapex === "" ? null : Number(form.budgetCapex),
        budgetOpex: form.budgetOpex === "" ? null : Number(form.budgetOpex),
      });
      notify({ kind: "success", text: "Saved." }); reload();
    } catch (e) { notify({ kind: "error", text: e.message }); }
  };
  // The parent owns the notice, so a message set here survives `after` (onBack).
  const act = (fn, after) => async () => { setConfirming(null); try { const r = await fn(); notify({ kind: "success", text: r.message || "Done." }); if (after) after(); reload(); } catch (e) { notify({ kind: "error", text: e.message }); } };
  const submit = act(() => api.submitTenderRequest(tr.id), onBack);
  const post = act(() => api.postTenderRequest(tr.id));
  const cancel = act(() => api.cancelTenderRequest(tr.id), onBack);
  // Build VE: confirm first, then hand the user off to the created evaluation
  // instead of dumping them on the tender list (UX-12/52).
  const build = async () => {
    setConfirming(null);
    try {
      const ve = await api.createVendorEvaluation({ tenderRequestId: tr.id, title: tr.description, closingDate: tr.closingDate });
      if (onOpenDoc && ve && ve.id) { onOpenDoc("vendor-eval", ve.id); return; }
      notify({ kind: "success", text: `Vendor evaluation ${ve.evaluationNumber} created — open it from the Vendor Evaluation page to set up bids, criteria and evaluators.` });
      reload();
    } catch (e) { notify({ kind: "error", text: e.message }); }
  };
  // The requester cannot post their own non-urgent request (UX-31/51) — the
  // server enforces this; don't render a button that would 403.
  const isRequestor = me && me.id === tr.requestorId;
  const canPostThis = tr.status === "PENDING_POST" && can("tenderRequest", "post") && !(isRequestor && !tr.urgent);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <Btn variant="outline" onClick={onBack}>← Back</Btn>
        <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>{tr.requestNumber}</h1>
        <Badge status={tr.status} />
        {tr.urgent && <span style={{ fontSize: text.sm, fontWeight: 700, color: C.warning }}>⚡ Urgent</span>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {isDraft && can("tenderRequest", "edit") && <Btn variant="success" onClick={submit}>Submit</Btn>}
          {isDraft && tr.urgent && isRequestor && <Btn onClick={() => setConfirming("post")}>Post now</Btn>}
          {canPostThis && <Btn variant="success" onClick={() => setConfirming("post")}>Post</Btn>}
          {tr.status === "PENDING_POST" && isRequestor && !tr.urgent && (
            <span style={{ fontSize: text.sm, color: C.gray500, alignSelf: "center" }}>Awaiting posting by a user with the posting permission (not the requester).</span>
          )}
          {["DRAFT", "PENDING_POST"].includes(tr.status) && can("tenderRequest", "edit") && <Btn variant="danger" onClick={() => setConfirming("cancel")}>Cancel</Btn>}
          {tr.status === "POSTED" && can("tenderRequest", "convert") && <Btn onClick={() => setConfirming("build")}>Build Vendor Evaluation</Btn>}
        </div>
      </div>

      <ConfirmDialog
        open={confirming === "post"}
        title="Post this tender?"
        confirmLabel="Post"
        onConfirm={post}
        onCancel={() => setConfirming(null)}
      >
        {`Posting assigns the official ${tr.type} number and publishes the announcement. The request can no longer be edited afterwards.`}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming === "cancel"}
        title="Cancel this tender request?"
        confirmLabel="Cancel request"
        variant="danger"
        onConfirm={cancel}
        onCancel={() => setConfirming(null)}
      >
        {`${tr.requestNumber} will be marked Cancelled. The source AOR (${tr.aor?.aorNumber || "—"}) becomes available for a new tender request.`}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming === "build"}
        title="Build the vendor evaluation?"
        confirmLabel="Build evaluation"
        onConfirm={build}
        onCancel={() => setConfirming(null)}
      >
        {`This converts ${tr.requestNumber} (it becomes Converted) and creates a draft vendor evaluation for ${fmt(tr.total)}. You'll be taken to the new evaluation to set up bids, criteria and evaluators.`}
      </ConfirmDialog>

      <Notice notice={notice} onDismiss={() => notify(null)} />

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, fontSize: text.md }}>
          <div><span style={{ color: C.gray500 }}>From AOR:</span> {onOpenDoc && tr.aor?.id ? <DocLink onClick={() => onOpenDoc("aor", tr.aor.id)}>{tr.aor.aorNumber}</DocLink> : <strong>{tr.aor?.aorNumber}</strong>}</div>
          <div><span style={{ color: C.gray500 }}>Official no.:</span> <strong style={{ color: C.primary }}>{tr.itqNumber || "— (assigned at posting)"}</strong></div>
          <div><span style={{ color: C.gray500 }}>Budget Ref:</span> <strong style={{ color: C.primary }}>{tr.budgetRef}</strong></div>
          <div><span style={{ color: C.gray500 }}>Total:</span> <strong>{fmt(tr.total)}</strong></div>
          <div><span style={{ color: C.gray500 }}>Requestor:</span> <strong>{tr.requestor?.displayName}</strong></div>
          {tr.postedBy && <div><span style={{ color: C.gray500 }}>Posted by:</span> <strong>{tr.postedBy.displayName}</strong></div>}
          {tr.vendorEvaluation && (
            <div>
              <span style={{ color: C.gray500 }}>Vendor evaluation:</span>{" "}
              {onOpenDoc
                ? <DocLink onClick={() => onOpenDoc("vendor-eval", tr.vendorEvaluation.id)}>{tr.vendorEvaluation.evaluationNumber} ({statusLabel(tr.vendorEvaluation.status)})</DocLink>
                : <strong style={{ color: C.success }}>{tr.vendorEvaluation.evaluationNumber} ({statusLabel(tr.vendorEvaluation.status)})</strong>}
            </div>
          )}
        </div>
      </Card>

      {/* Part I */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 12 }}>Part I — Request details</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <label style={label} htmlFor="dtr-type">Type</label>
            <select id="dtr-type" value={form.type} disabled={!isDraft} onChange={(e) => set("type", e.target.value)} style={inputStyle}>
              <option value="ITQ">ITQ — Invitation to Quote</option>
              <option value="ITT">ITT — Invitation to Tender</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <label style={{ fontSize: text.md, display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={form.urgent} disabled={!isDraft} onChange={(e) => set("urgent", e.target.checked)} /> Urgent
            </label>
          </div>
          <div><label style={label} htmlFor="dtr-contact-name">Contact name</label><input id="dtr-contact-name" value={form.contactName} disabled={!isDraft} onChange={(e) => set("contactName", e.target.value)} style={inputStyle} /></div>
          <div><label style={label} htmlFor="dtr-contact-email">Contact email</label><input id="dtr-contact-email" value={form.contactEmail} disabled={!isDraft} onChange={(e) => set("contactEmail", e.target.value)} style={inputStyle} /></div>
          <div><label style={label} htmlFor="dtr-publication-date">Publication / start date</label><DateField id="dtr-publication-date" value={form.publicationDate} onChange={(v) => set("publicationDate", v)} disabled={!isDraft} /></div>
          <div>
            <span style={label}>Closing date <span style={{ fontWeight: 600, textTransform: "none", color: C.gray500 }}>({form.type} — {form.type === "ITT" ? 14 : 7} SG working days, 23:59)</span></span>
            <div style={{ ...inputStyle, background: C.gray100, color: closing.err ? C.danger : (closing.iso ? C.gray700 : C.gray400), display: "flex", alignItems: "center", minHeight: 19 }}>
              {closing.err || (closing.iso ? `${fmtDMY(closing.iso)} 23:59` : "— set a publication date —")}
            </div>
          </div>
          <div><label style={label} htmlFor="dtr-budget-capex">Approved budget — Capex</label><input id="dtr-budget-capex" type="number" value={form.budgetCapex} disabled={!isDraft} onChange={(e) => set("budgetCapex", e.target.value)} style={inputStyle} /></div>
          <div><label style={label} htmlFor="dtr-budget-opex">Approved budget — Opex</label><input id="dtr-budget-opex" type="number" value={form.budgetOpex} disabled={!isDraft} onChange={(e) => set("budgetOpex", e.target.value)} style={inputStyle} /></div>
          <div style={{ gridColumn: "1 / -1" }}><label style={label} htmlFor="dtr-description">Description of product / service</label><textarea id="dtr-description" value={form.description} disabled={!isDraft} onChange={(e) => set("description", e.target.value)} style={{ ...inputStyle, minHeight: 70 }} /></div>
        </div>
      </Card>

      {/* Part II */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 8 }}>Part II — Announcement {tr.urgent && <span style={{ color: C.gray500, fontWeight: 600 }}>(optional for urgent requests)</span>}</div>
        <textarea aria-label="Part II announcement" value={form.announcement} disabled={!isDraft} onChange={(e) => set("announcement", e.target.value)} style={{ ...inputStyle, minHeight: 140 }} />
      </Card>

      {isDraft && can("tenderRequest", "edit") && <Btn onClick={save}>Save changes</Btn>}
    </div>
  );
}
